import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@ds';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FeatureFlags } from 'src/utils/constants/FeatureFlags';
import { useGetInstanceConfig } from 'src/shared/queries/useGetInstanceConfig';

declare global {
  interface Window {
    $sherpa: any;
  }
}

const SherpaTripElementId = 'sherpa-trip-element';

const SherpaTripElement = () => {
  const [isLoading, setIsLoading] = useState(true);
  const observerRef = useRef<MutationObserver | null>(null);
  const timerRef = useRef<number | null>(null);
  const flags = useFlags();
  const navigate = useNavigate();
  const { data: instanceConfig } = useGetInstanceConfig();

  const initializeSherpa = () => {
    if (!window.$sherpa) {
      console.warn('Sherpa SDK not available');
      return;
    }

    // Check if Sherpa element is already initialized to prevent duplicate mounting
    const targetElement = document.getElementById(SherpaTripElementId);
    if (!targetElement) {
      console.warn('Sherpa target element not found');
      return;
    }

    // Check if already has Sherpa content
    if (targetElement.hasChildNodes() && targetElement.querySelector('[data-sherpa]')) {
      return;
    }

    try {
      const elementConfig = { placement: 'mmb' };
      window.$sherpa.V2.createElement('trip', elementConfig).mount(`#${SherpaTripElementId}`);
    } catch (error) {
      console.error('Failed to initialize Sherpa:', error);
    }
  };

  useEffect(() => {
    if (!flags) return;

    if (!flags[FeatureFlags.FNXHideSherpa] || (instanceConfig && instanceConfig.immigrationInstance !== 1)) {
      navigate('/');
    }
  }, [flags, navigate, instanceConfig]);

  // Wait for SDK to be ready and initialize Sherpa
  useEffect(() => {
    if (!flags || !flags[FeatureFlags.FNXHideSherpa]) return;

    initializeSherpa();
  }, [flags]);

  // Observe changes to the Sherpa container to determine when it has loaded, display a skeleton until then
  useEffect(() => {
    if (!flags || !flags[FeatureFlags.FNXHideSherpa]) {
      setIsLoading(false);
      return;
    }

    const targetNode = document.getElementById(SherpaTripElementId);
    if (!targetNode) return;

    // Set up observer to detect when Sherpa content is loaded
    observerRef.current = new MutationObserver(() => {
      if (targetNode.childNodes.length > 0 || targetNode.attributes.length > 2) {
        setIsLoading(false);
        observerRef.current?.disconnect();
      }
    });

    observerRef.current.observe(targetNode, {
      childList: true,
      attributes: true,
      subtree: true,
    });

    // Fallback timeout to stop loading state
    timerRef.current = window.setTimeout(() => {
      setIsLoading(false);
      observerRef.current?.disconnect();
    }, 3000); // 3 second timeout

    return () => {
      observerRef.current?.disconnect();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [flags]);

  return (
    <>
      {isLoading && <Skeleton width="100%" height="40px" />}
      <div id={SherpaTripElementId} />
    </>
  );
};

export default SherpaTripElement;
